import {
  Badge,
  Button,
  Calendar,
  CalendarProps,
  Col,
  Input,
  Popover,
  Row,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd"
import { useTranslation } from "react-i18next"
import { ReactComponent as TableIcon } from "assets/icons/table.svg"
import { ReactComponent as CalendarIcon } from "assets/icons/calendar.svg"
import { ReactComponent as ShowIcon } from "assets/icons/show.svg"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"

import styles from "./styles.module.scss"
import { useEffect, useMemo, useState } from "react"
import CircleButton from "@atoms/icon-button"
import { formatDay } from "utils/format-datetime"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useMemberId } from "pages/member"
import { useCalendarPresubscriptions, usePresubscriptions } from "./queries"
import { useEventsTab } from ".."
import { usePopup } from "utils/use-popup"
import { IPresubscription } from "pages/cart/types"
import DeletePresubscriptionModal from "./deletePresubscription"
import dayjs, { Dayjs } from "dayjs"
import Loader from "components/loader"

const { Text, Title } = Typography
const { Search } = Input

const ACTIVITY_PARAM = "q"
const VIEW_PARAM = "view"

const viewType = ["table", "calendar"] as const
type ViewType = typeof viewType
const isViewType = (view: any): view is ViewType => viewType.includes(view)

const Presubscriptions = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const activeTab = useEventsTab()
  const memberId = useMemberId()
  const [searchParams, setSearchParams] = useSearchParams()
  const [calendarValue, setCalendarValue] =
    useState<CalendarProps<Dayjs>["value"]>(dayjs)

  const memberPresubscriptions = usePresubscriptions(memberId!, undefined, {
    enabled: searchParams.get("view") !== "calendar",
  })

  const calendarPresubscriptions = useCalendarPresubscriptions(
    memberId!,
    {
      view: "month",
      date: calendarValue!.format("YYYY-MM-DD"),
    },
    {
      enabled: searchParams.get("view") === "calendar",
    }
  )

  const deletePresubscriptionModal = usePopup<IPresubscription>()

  const filteredPresubscriptions = useMemo(() => {
    if (searchParams.get(ACTIVITY_PARAM)) {
      return memberPresubscriptions.data
        ?.filter(
          (element) =>
            element.event.name
              .toLowerCase()
              .startsWith(searchParams.get(ACTIVITY_PARAM)!.toLowerCase()) &&
            element
        )
        .sort((a, b) => a.event.name.localeCompare(b.event.name))
    }
    return memberPresubscriptions.data
  }, [memberPresubscriptions.data, searchParams])

  const dateCellRender = (value: any) => {
    return (
      <ul className="events">
        {calendarPresubscriptions.data?.map((item) => {
          if (item.event.start_date === value.format("YYYY-MM-DD")) {
            return (
              <Popover
                key={item.id}
                content={() => (
                  <div>
                    <Title level={5}>{item.event.name}</Title>
                    <Text>
                      {item.event.start_hour} a {item.event.end_hour}
                    </Text>
                  </div>
                )}
              >
                <li
                  onClick={() => navigate(`../show/${item.event.id}`)}
                  className={styles.calendarItemContainer}
                >
                  <div className={styles.calendarItem}>
                    <span />
                    <p>{item.event.name}</p>
                  </div>
                </li>
              </Popover>
            )
          } else {
            return null
          }
        })}
      </ul>
    )
  }

  useEffect(() => {
    // added tab because useEffect is running event if the tab is not selected
    if (
      !isViewType(searchParams.get(VIEW_PARAM)) &&
      activeTab === "presubscriptions"
    ) {
      searchParams.set(VIEW_PARAM, "table")
      setSearchParams(searchParams, { replace: true })
    }
  })

  return (
    <>
      <Row style={{ marginBottom: 15 }} justify={"space-between"}>
        <Col>
          <Space>
            {searchParams.get(VIEW_PARAM) === "table" && (
              <Search
                value={searchParams.get(ACTIVITY_PARAM) || ""}
                onChange={(e) => {
                  const newValue = e.target.value
                  newValue !== ""
                    ? searchParams.set(ACTIVITY_PARAM, newValue)
                    : searchParams.delete(ACTIVITY_PARAM)
                  setSearchParams(searchParams, { replace: true })
                }}
                placeholder={t("global.search")}
                style={{ width: "100%" }}
              />
            )}
          </Space>
        </Col>
        <Col>
          <Space>
            <Tooltip
              title={t(
                searchParams.get(VIEW_PARAM) === "table"
                  ? "global.calendar"
                  : "global.table"
              )}
            >
              <CircleButton
                onClick={() => {
                  searchParams.set(
                    VIEW_PARAM,
                    searchParams.get(VIEW_PARAM) === "table"
                      ? "calendar"
                      : "table"
                  )
                  setSearchParams(searchParams, { replace: true })
                }}
                icon={
                  searchParams.get(VIEW_PARAM) === "table" ? (
                    <CalendarIcon />
                  ) : (
                    <TableIcon />
                  )
                }
              />
            </Tooltip>
          </Space>
        </Col>
      </Row>
      {searchParams.get(VIEW_PARAM) === "calendar" ? (
        <div className={styles.calendarMonthMode}>
          <Loader
            spinProps={{
              spinning:
                calendarPresubscriptions.isLoading ||
                calendarPresubscriptions.isPreviousData,
            }}
          >
            <Calendar
              cellRender={dateCellRender}
              mode="month"
              value={calendarValue}
              onChange={setCalendarValue}
            />
          </Loader>
        </div>
      ) : (
        <Table
          loading={memberPresubscriptions.isLoading}
          pagination={false}
          tableLayout="fixed"
          className={styles.table}
          rowKey={"id"}
          columns={[
            {
              title: t<string>("events.activities"),
              dataIndex: ["event", "name"],
              key: "activities",
              ellipsis: true,
              render: (value) => (
                <Tooltip placement="bottomLeft" title={value}>
                  {value}
                </Tooltip>
              ),
            },
            {
              title: t<string>("events.title"),
              dataIndex: "start_date",
              key: "events",
              ellipsis: true,
              render: (text, record) => {
                const value = `${formatDay(record.event.start_date)}, ${
                  record.event.start_hour
                }h - ${record.event.end_hour}h`
                return (
                  <Tooltip placement="bottomLeft" title={value}>
                    {value}
                  </Tooltip>
                )
              },
            },
            {
              title: t<string>("events.status"),
              dataIndex: "displayed_status",
              key: "status",
              ellipsis: true,
              render: (_, record) => (
                <Tooltip placement="bottomLeft" title={record.comment}>
                  <Badge>{record.displayed_status}</Badge>
                </Tooltip>
              ),
            },
            {
              title: t<string>("global.actions"),
              key: "action",
              width: 100,
              render: (_, record) => (
                <>
                  <Tooltip placement="bottom" title={t("global.show")}>
                    <Button
                      type="text"
                      shape="circle"
                      onClick={() => navigate(`../show/${record.event.id}`)}
                      icon={<ShowIcon />}
                    />
                  </Tooltip>
                  <Tooltip
                    placement="bottom"
                    title={t("global.deleteX", {
                      entity: t("events.presubscription"),
                      context: "female",
                      count: 1,
                    })}
                  >
                    <Button
                      type="text"
                      shape="circle"
                      disabled={
                        !["created", "waiting"].includes(record.status!)
                      }
                      onClick={() => deletePresubscriptionModal.open(record)}
                      icon={<DeleteIcon />}
                    />
                  </Tooltip>
                </>
              ),
            },
          ]}
          dataSource={filteredPresubscriptions}
        />
      )}
      <DeletePresubscriptionModal {...deletePresubscriptionModal} />
    </>
  )
}

export default Presubscriptions
